<template>
    <div class="support-chat" :class="{'support-chat--floating': floating}">
        <v-btn v-if="floating" class="support-chat__launcher" color="primary" fab :aria-label="open ? 'إغلاق محادثة الدعم' : 'فتح محادثة الدعم'" :aria-expanded="String(open)" @click="toggle">
            <v-badge :value="notifications.unread > 0" :content="notifications.unread" color="error"><v-icon>{{open ? 'mdi-close' : 'mdi-message-processing-outline'}}</v-icon></v-badge>
        </v-btn>
        <section v-if="started" v-show="!floating || open" class="support-chat__panel" aria-label="محادثة الدعم">
            <div class="d-flex align-center pa-2 surface">
                <strong class="px-2">محادثة الدعم</strong><v-spacer />
                <v-btn v-if="floating" icon aria-label="إغلاق محادثة الدعم" @click="toggle"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <div v-if="loading" class="pa-4 text-center" role="status" aria-live="polite"><v-progress-circular indeterminate color="primary" size="24" /><p class="mt-2">جارٍ تحميل محادثة الدعم…</p></div>
            <v-alert v-if="failed" type="warning" text class="ma-3">
                تعذر تحميل المحادثة.
                <v-btn text small color="primary" @click="start">إعادة المحاولة</v-btn>
                <v-btn v-if="floating" text small color="primary" :to="{name: 'help'}" @click="open = false">إرسال طلب مساعدة</v-btn>
            </v-alert>
            <div class="px-3 py-1 d-flex align-center justify-center">
                <v-btn v-if="config.pushEnabled && pushStatus.available && (pushStatus.permission === 'default' || pushStatus.needsOptIn)" text small color="primary" @click="enablePush">تفعيل إشعارات ردود الدعم</v-btn>
                <span v-else-if="config.pushEnabled && pushStatus.permission === 'denied'" class="caption muted--text">إشعارات المتصفح محظورة. يمكنك السماح بها من إعدادات الموقع.</span>
                <span v-else-if="config.pushEnabled && pushStatus.error" class="caption muted--text">تعذر تفعيل الإشعارات. أعد فتح التطبيق ثم <v-btn text small color="primary" @click="enablePush">حاول مجددًا</v-btn></span>
                <span v-else-if="pushStatus.subscribed" class="caption muted--text">إشعارات ردود الدعم مفعّلة</span>
                <v-btn icon small :aria-label="notifications.muted ? 'تشغيل صوت إشعارات الدعم' : 'كتم صوت إشعارات الدعم'" @click="toggleSound"><v-icon small>{{ notifications.muted ? 'mdi-volume-off' : 'mdi-volume-high' }}</v-icon></v-btn>
            </div>
            <iframe v-if="!failed" v-show="!loading" :key="frameVersion" ref="frame" title="محادثة الدعم" allow="microphone; autoplay" :aria-busy="String(loading)" @error="fail" />
        </section>
        <v-btn v-if="!floating" text color="primary" class="mt-3" @click="$emit('unavailable')">المحادثة لا تعمل؟ إرسال طلب مساعدة</v-btn>
    </div>
</template>

<script>
import Vue from 'vue';
import { supportNotifications, clearSupportUnread, notifySupportReply, unlockSupportSound } from '@/services/support-notifications';
const notifications = Vue.observable(supportNotifications);
import { createAgentplaneFrame } from '@/services/support-chat';

export default {
    props: {
        config: { type: Object, required: true },
        user: { default: null },
        floating: { type: Boolean, default: false },
    },
    data: () => ({ open: false, frameVersion: 0, started: false, loading: false, failed: false, notifications, pushStatus: { available: false, permission: 'unsupported', error: false } }),
    watch: {
        user: { deep: true, handler(value, old) { if ((value && value.id) !== (old && old.id)) clearSupportUnread(window); if (this.client) this.client.updateUser(value); } },
        'notifications.openRequest'() { if (this.floating) this.showChat(); },
        $route() { if (this.client) this.client.page(); },
    },
    mounted() {
        this.open = this.floating && this.$route.query.supportChat === '1';
        document.addEventListener('visibilitychange', this.visibilityChanged);
        this.start();
    },
    beforeDestroy() {
        document.removeEventListener('visibilitychange', this.visibilityChanged);
        notifications.visible = false;
        if (this.client) this.client.dispose();
    },
    methods: {
        visibilityChanged() {
            const visible = (!this.floating || this.open) && !document.hidden;
            notifications.visible = visible;
            if (visible) clearSupportUnread(window);
            if (this.client) this.client.setVisible(!this.floating || this.open);
        },
        showChat() { this.open = true; this.visibilityChanged(); },
        enablePush() { unlockSupportSound(window); if (this.client) this.client.enablePush(); },
        toggleSound() {
            unlockSupportSound(window);
            notifications.muted = !notifications.muted;
            try { localStorage.setItem('derasa-support-muted', String(notifications.muted)); } catch (error) { /* optional */ }
        },
        toggle() {
            this.open = !this.open;
            unlockSupportSound(window);
            this.visibilityChanged();
            if (!this.started) this.start();
            else if (this.client) this.client.setVisible(this.open);
        },
        fail() {
            if (this.client) this.client.dispose();
            this.loading = false;
            this.failed = true;
            this.$emit('unavailable');
        },
        start() {
            if (this.client) this.client.dispose();
            this.frameVersion++;
            this.started = true;
            this.failed = false;
            this.loading = true;
            this.$nextTick(() => {
                if (this._isBeingDestroyed || this._isDestroyed || !this.$refs.frame) return;
                this.client = createAgentplaneFrame({
                    host: window, frame: this.$refs.frame, config: this.config, user: this.user,
                    visible: !this.floating || this.open,
                    onReady: () => { this.loading = false; this.visibilityChanged(); this.$emit('ready'); },
                    onReset: () => { this.loading = true; },
                    onRestart: this.start,
                    onPushStatus: value => { this.pushStatus = { ...value, error: value.error || false }; },
                    onOpen: this.showChat,
                    onError: this.fail,
                    onClose: () => { this.open = false; this.visibilityChanged(); },
                    onUnread: () => notifySupportReply(window),
                });
            });
        },
    },
};
</script>

<style scoped>
.support-chat__panel { display: flex; flex-direction: column; height: 70vh; height: 70dvh; min-height: 420px; overflow: hidden; background: var(--v-surface-base, #fff); border: 1px solid #ddd; border-radius: 12px; }
.support-chat__panel iframe { width: 100%; flex: 1; min-height: 0; border: 0; }
.support-chat--floating { position: fixed; bottom: 88px; right: 18px; z-index: 100; }
.support-chat--floating .support-chat__panel { position: absolute; bottom: 70px; right: 0; width: 390px; height: min(620px, calc(100dvh - 190px)); min-height: 240px; box-shadow: 0 8px 35px #0003; }
@media (max-width: 600px) {
    .support-chat--floating .support-chat__panel { width: calc(100vw - 24px); right: -6px; }
}
</style>
